import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  ExpandMore,
  Search,
  FilterList,
  BugReport,
  Add,
  Update,
  Security as SecurityIcon,
  Remove,
  Warning,
  Timeline as TimelineIcon
} from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { getSystemChangelog } from "../../services/system.api";
import renderMarkdownInline from "../../helpers/renderMarkdownInline";
import toastError from "../../errors/toastError";

// Paleta por categoría (hex, consistente en claro/oscuro).
const CATEGORY_META = {
  added: { label: "Nuevas funcionalidades", color: "#16a34a", Icon: Add },
  changed: { label: "Cambios", color: "#2563eb", Icon: Update },
  fixed: { label: "Correcciones", color: "#f59e0b", Icon: BugReport },
  security: { label: "Seguridad", color: "#dc2626", Icon: SecurityIcon },
  deprecated: { label: "Obsoletos", color: "#9333ea", Icon: Warning },
  removed: { label: "Eliminados", color: "#6b7280", Icon: Remove }
};
const CATEGORY_ORDER = ["added", "changed", "fixed", "security", "deprecated", "removed"];

// Tipo de release derivado de SemVer.
const getVersionType = (version) => {
  const [, minor, patch] = String(version).split(".").map((n) => parseInt(n, 10));
  if (minor === 0 && patch === 0) return "MAJOR";
  if (patch === 0) return "MINOR";
  return "PATCH";
};
const TYPE_META = {
  MAJOR: { color: "#7c3aed", bg: "linear-gradient(135deg,#7c3aed 0%,#4f46e5 55%,#2563eb 100%)" },
  MINOR: { color: "#2563eb", bg: "#2563eb" },
  PATCH: { color: "#6b7280", bg: "#6b7280" }
};

const useStyles = makeStyles((theme) => {
  const dark = theme.mode === "dark";
  const rail = dark ? "#374151" : "#e2e8f0";
  return {
    root: {
      padding: theme.spacing(3),
      backgroundColor: dark ? "#111827" : "#f5f7fb",
      minHeight: "100vh"
    },
    header: {
      marginBottom: theme.spacing(3),
      background: "linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)",
      color: "#fff",
      padding: theme.spacing(3),
      borderRadius: 16,
      boxShadow: "0 12px 32px rgba(79,70,229,0.35)"
    },
    headerTop: { display: "flex", alignItems: "center", gap: theme.spacing(2), marginBottom: theme.spacing(2.5) },
    statRow: { display: "flex", flexWrap: "wrap", gap: theme.spacing(2) },
    statCard: {
      flex: "1 1 180px",
      background: "rgba(255,255,255,0.14)",
      borderRadius: 12,
      padding: theme.spacing(1.75, 2),
      backdropFilter: "blur(4px)"
    },
    statValue: { fontSize: 26, fontWeight: 800, lineHeight: 1.1 },
    statLabel: { fontSize: 12, opacity: 0.9, textTransform: "uppercase", letterSpacing: 0.6 },
    searchContainer: {
      marginBottom: theme.spacing(3),
      display: "flex",
      gap: theme.spacing(2),
      alignItems: "center",
      flexWrap: "wrap",
      padding: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      borderRadius: 12,
      border: `1px solid ${rail}`,
      boxShadow: dark ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.06)"
    },
    // Timeline
    timeline: { position: "relative", paddingLeft: theme.spacing(4) },
    rail: {
      position: "absolute",
      left: 14,
      top: 8,
      bottom: 8,
      width: 2,
      backgroundColor: rail
    },
    node: { position: "relative", marginBottom: theme.spacing(2.5) },
    dot: {
      position: "absolute",
      left: -35,
      top: 18,
      width: 16,
      height: 16,
      borderRadius: "50%",
      border: `3px solid ${theme.palette.background.paper}`,
      boxShadow: "0 0 0 2px currentColor"
    },
    card: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 12,
      border: `1px solid ${rail}`,
      boxShadow: dark ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.06)",
      overflow: "hidden",
      transition: "box-shadow .2s ease, transform .2s ease",
      "&:hover": { transform: "translateY(-2px)", boxShadow: dark ? theme.shadows[10] : theme.shadows[6] }
    },
    cardLatest: {
      border: "1px solid transparent",
      backgroundImage: `linear-gradient(${theme.palette.background.paper},${theme.palette.background.paper}), linear-gradient(135deg,#7c3aed,#2563eb)`,
      backgroundOrigin: "border-box",
      backgroundClip: "padding-box, border-box",
      boxShadow: "0 10px 30px rgba(124,58,237,0.25)"
    },
    cardHead: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      padding: theme.spacing(2),
      cursor: "pointer",
      flexWrap: "wrap"
    },
    verChip: { fontWeight: 800, fontSize: "1rem", color: "#fff" },
    typeBadge: { fontWeight: 800, fontSize: 10, letterSpacing: 0.6, color: "#fff", height: 20 },
    date: { color: theme.palette.text.secondary, fontSize: 14, fontWeight: 500 },
    changesPill: {
      backgroundColor: dark ? "#374151" : "#eef2ff",
      color: dark ? "#c7d2fe" : "#4f46e5",
      borderRadius: 999,
      padding: theme.spacing(0.25, 1.25),
      fontSize: 12,
      fontWeight: 700
    },
    body: { padding: theme.spacing(0, 2, 2, 2) },
    catSummary: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap", marginBottom: theme.spacing(2) },
    catBlock: { marginBottom: theme.spacing(2) },
    catTitle: { display: "flex", alignItems: "center", gap: theme.spacing(1), fontWeight: 700, fontSize: 14, marginBottom: theme.spacing(1) },
    item: {
      borderLeftWidth: 3,
      borderLeftStyle: "solid",
      paddingLeft: theme.spacing(1.5),
      marginBottom: theme.spacing(0.75),
      lineHeight: 1.5,
      color: theme.palette.text.primary,
      fontSize: 13.5
    },
    loadingContainer: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", gap: theme.spacing(2) },
    field: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: dark ? "#1f2937" : "#fff",
        color: theme.palette.text.primary,
        "& fieldset": { borderColor: rail },
        "&:hover fieldset": { borderColor: theme.palette.primary.main },
        "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main }
      },
      "& .MuiInputLabel-root": { color: theme.palette.text.secondary }
    }
  };
});

const ReleasesPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [changelogData, setChangelogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [expandedVersion, setExpandedVersion] = useState(null);

  useEffect(() => {
    loadChangelog();
  }, []);

  const loadChangelog = async () => {
    try {
      setLoading(true);
      const { data } = await getSystemChangelog();
      setChangelogData(data);
      // Expandir la última versión por defecto.
      if (data?.versions?.[0]?.version) setExpandedVersion(data.versions[0].version);
    } catch (err) {
      setError(err.response?.data?.error || "Error al cargar el changelog");
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionToggle = (version) => {
    setExpandedVersion(expandedVersion === version ? null : version);
  };

  const filteredVersions = changelogData?.versions?.filter((version) => {
    const matchesSearch =
      searchTerm === "" ||
      version.version.includes(searchTerm) ||
      version.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      filterCategory === "all" || version.categories[filterCategory]?.length > 0;
    return matchesSearch && matchesCategory;
  }) || [];

  const totalChanges =
    changelogData?.versions?.reduce((t, v) => t + (v.totalChanges || 0), 0) || 0;
  const latestVersion = changelogData?.versions?.[0]?.version || "N/A";
  const latestType = latestVersion !== "N/A" ? getVersionType(latestVersion) : null;

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={54} />
        <Typography variant="h6" style={{ color: theme.palette.text.primary }}>
          Cargando historial de versiones…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Alert severity="error">
          <Typography variant="h6">Error al cargar versiones</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header + estadísticas */}
      <Paper className={classes.header} elevation={0}>
        <Box className={classes.headerTop}>
          <TimelineIcon style={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h4" component="h1" style={{ fontWeight: 800 }}>
              Historial de Versiones
            </Typography>
            <Typography variant="subtitle1" style={{ opacity: 0.92 }}>
              Evolución del producto — trazabilidad completa de cambios y mejoras
            </Typography>
          </Box>
        </Box>
        <Box className={classes.statRow}>
          <Box className={classes.statCard}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography className={classes.statValue}>v{latestVersion}</Typography>
              {latestType && (
                <Chip
                  size="small"
                  label={latestType}
                  className={classes.typeBadge}
                  style={{ background: "rgba(255,255,255,0.25)" }}
                />
              )}
            </Box>
            <Typography className={classes.statLabel}>Última versión</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography className={classes.statValue}>{totalChanges}</Typography>
            <Typography className={classes.statLabel}>Total de cambios</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography className={classes.statValue}>{changelogData?.versions?.length || 0}</Typography>
            <Typography className={classes.statLabel}>Versiones publicadas</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Búsqueda + filtro */}
      <Paper className={classes.searchContainer} elevation={0}>
        <TextField
          placeholder="Buscar versiones o cambios…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search style={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            )
          }}
          className={classes.field}
          style={{ flex: 1, minWidth: 240 }}
        />
        <FormControl className={classes.field} style={{ minWidth: 220 }} variant="outlined" size="small">
          <InputLabel>Categoría</InputLabel>
          <Select
            value={filterCategory}
            label="Categoría"
            onChange={(e) => setFilterCategory(e.target.value)}
            startAdornment={<FilterList style={{ color: theme.palette.text.secondary, marginRight: 4 }} />}
          >
            <MenuItem value="all">Todas las categorías</MenuItem>
            {CATEGORY_ORDER.map((c) => (
              <MenuItem key={c} value={c}>{CATEGORY_META[c].label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Timeline de versiones */}
      {filteredVersions.length === 0 ? (
        <Paper elevation={0} style={{ padding: 32, textAlign: "center", backgroundColor: theme.palette.background.paper, borderRadius: 12 }}>
          <Typography variant="h6" style={{ color: theme.palette.text.primary }}>
            No se encontraron versiones que coincidan con los filtros
          </Typography>
        </Paper>
      ) : (
        <Box className={classes.timeline}>
          <span className={classes.rail} />
          {filteredVersions.map((version, index) => {
            const type = getVersionType(version.version);
            const tMeta = TYPE_META[type];
            const isLatest = index === 0 && searchTerm === "" && filterCategory === "all";
            const open = expandedVersion === version.version;
            return (
              <Box className={classes.node} key={`${version.version}-${version.date}-${index}`}>
                <span className={classes.dot} style={{ color: tMeta.color, backgroundColor: tMeta.color }} />
                <Box className={`${classes.card} ${isLatest ? classes.cardLatest : ""}`}>
                  <Box className={classes.cardHead} onClick={() => handleVersionToggle(version.version)}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip label={`v${version.version}`} className={classes.verChip} style={{ background: tMeta.bg }} />
                      <Chip size="small" label={type} className={classes.typeBadge} style={{ backgroundColor: tMeta.color }} />
                      <Typography className={classes.date}>{version.date}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span className={classes.changesPill}>{version.totalChanges} cambios</span>
                      <IconButton size="small" style={{ color: theme.palette.text.primary, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
                        <ExpandMore />
                      </IconButton>
                    </Box>
                  </Box>

                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box className={classes.body}>
                      {version.totalChanges > 0 ? (
                        <>
                          <Box className={classes.catSummary}>
                            {CATEGORY_ORDER.map((cat) => {
                              const items = version.categories[cat] || [];
                              if (!items.length) return null;
                              const m = CATEGORY_META[cat];
                              return (
                                <Chip
                                  key={cat}
                                  size="small"
                                  label={`${m.label}: ${items.length}`}
                                  style={{ backgroundColor: `${m.color}22`, color: m.color, fontWeight: 700, border: `1px solid ${m.color}55` }}
                                />
                              );
                            })}
                          </Box>
                          {CATEGORY_ORDER.map((cat) => {
                            const items = version.categories[cat] || [];
                            if (!items.length) return null;
                            const m = CATEGORY_META[cat];
                            const Icon = m.Icon;
                            return (
                              <Box className={classes.catBlock} key={cat}>
                                <Typography className={classes.catTitle} style={{ color: m.color }}>
                                  <Icon style={{ fontSize: 18 }} /> {m.label}
                                </Typography>
                                <Box>
                                  {items.map((item, i) => (
                                    <Typography key={i} className={classes.item} style={{ borderLeftColor: m.color }}>
                                      {renderMarkdownInline(item)}
                                    </Typography>
                                  ))}
                                </Box>
                              </Box>
                            );
                          })}
                        </>
                      ) : (
                        <Box style={{ textAlign: "center", padding: 24 }}>
                          <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                            <span role="img" aria-label="doc">📋</span> Esta versión no tiene cambios documentados en el changelog.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ReleasesPage;
